import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useRouteLoaderData } from "react-router"
import { Heading } from "@tutellus/tutellus-components"
import { Container } from "../../components/ui/container"
import { useLayout } from "../../context"
import { WalletTokensTable } from "../../components/modules/WalletTokensTable"
import { WalletPoolStatsSimpleTable } from "../../components/modules/WalletPoolStatsSimpleTable"
import { useData } from "../../context/DataContext"
import { WalletNFTTable } from "../../components/modules/WalletNFTTable"
import { RootDataLoader } from "../DataRoot/loader"
import { useFormatAmounts } from "../../hooks/useFormatAmounts"
import styles from "./index.module.css"

export function Component() {
  const { userBalances, poolStatsList, userPoolStatsList, userDeposits } = useData()
  const { projectConfig } = useRouteLoaderData("root") as RootDataLoader
  const { t } = useTranslation("wallet")
  const { currencyID } = useLayout()
  const { formatCurrencyAmount } = useFormatAmounts()

  const totalBalance = useMemo(() => {
    if (!userBalances) return 0

    const tokensBalance = userBalances.balances.reduce((total, balance) => {
      const currencyAmount = balance.currencyAmounts?.find((c) => c.currencyID === currencyID)?.number || 0
      return total + currencyAmount
    }, 0)

    const poolsBalance = poolStatsList?.items.reduce((total, pool) => {
      const userPoolStats = userPoolStatsList?.items.find((ups) => ups.poolID === pool.id)
      const depositedAmount =
        userPoolStats?.deposited?.currencyAmounts?.find((c) => c.currencyID === currencyID)?.number || 0
      return total + depositedAmount
    }, 0)

    const nftsBalance =
      userDeposits?.items.reduce((total, deposit) => {
        const valueAmount = deposit.deposited.currencyAmounts?.find((c) => c.currencyID === currencyID)?.number || 0
        return total + valueAmount
      }, 0) || 0

    return tokensBalance + (poolsBalance || 0) + nftsBalance
  }, [userBalances, poolStatsList, userPoolStatsList, userDeposits, currencyID])

  const formattedTotalBalance = formatCurrencyAmount(totalBalance)

  if (!userBalances) return null

  const hasTokens = userBalances.balances.some((balance) => balance.number > 0)
  const hasNFTs = userDeposits && userDeposits.items.some((deposit) => deposit.deposited.number > 0)
  const hasPools = userPoolStatsList && userPoolStatsList?.items.some((ups) => ups.deposited.number > 0)

  return (
    <Container mb="xl" grid={1}>
      <Heading as="h1" align="center">
        {t("title")}
      </Heading>

      <div className={styles.totalBalance}>
        <Heading as="h2" align="center" color="primary">
          {t("totalBalance")}
        </Heading>
        <span className={styles.balance}>{formattedTotalBalance}</span>
      </div>
      {hasNFTs && (
        <div className={styles.walletTable}>
          <WalletNFTTable userDeposits={userDeposits} currencyID={currencyID} />
        </div>
      )}
      {hasTokens && (
        <div className={styles.walletTable}>
          <WalletTokensTable userBalances={userBalances} projectConfig={projectConfig} />
        </div>
      )}
      {hasPools && (
        <div className={styles.walletTable}>
          <WalletPoolStatsSimpleTable userPoolStatsList={userPoolStatsList.serialize()} />
        </div>
      )}
    </Container>
  )
}
